import { DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { DateTime, DateTimeFormatOptions } from 'luxon';
import { BehaviorSubject } from 'rxjs';
import { IEidosFormats } from './environment.interface';
import { EidosConfigService } from './eidos-config.service';

@Injectable({
  providedIn: 'root'
})
export class CoreFormatService {
  /**
   * Default formats config
   *
   * @type {IEidosFormats}
   * @memberof CoreFormatService
   */
  private readonly DEFAULT_FORMATS: IEidosFormats = {
    defaultCurrency: 'USD',
    dateAndTime: 'dd/MM/yyyy HH:mm',
    dateFmt: 'dd/MM/yyyy',
    dateAndMonth: 'dd MMM',
    dateAndMonthAndYear: 'dd MMM yyyy',
    dateQueryParams: 'yyyy-MM-dd', // Format used for query date params
    dateQueryApiFormat: 'yyyy-MM-dd HH:mm:ss', // Format used for API date params
    dateStandardLocaleOpts: { locale: 'en' },
    amountStandardFormatOpts: '1.2-2',
    amountStandardLocaleOpts: 'en',

    currencies: {
      USD: "$",
      EUR: "€",
      GBP: "£",
      AUD: "A$",
      CAD: "C$",
    },
  }
  /**
   * Current formats config
   *
   * @type {BehaviorSubject<IEidosFormats>}
   * @memberof CoreFormatService
   */
  private formats: BehaviorSubject<IEidosFormats> = new BehaviorSubject<IEidosFormats>(this.DEFAULT_FORMATS);
  /**
   * Internal decimal pipe
   *
   * @type {DecimalPipe}
   * @memberof CoreFormatService
   */
  private decimalPipe: DecimalPipe = new DecimalPipe('en');

  constructor(protected coreConfigService: EidosConfigService) {
    this.coreConfigService.currentConfig.subscribe(config => {
      this.formats.next(config.formats);
    });
  }

  private getFromConfig(key: string): any {
    const formats = this.formats.getValue();
    return !!formats.hasOwnProperty(key) ? (<any>formats)[key] : undefined;
  }

  public defaultCurrency(): string {
    return this.getFromConfig('defaultCurrency');
  }

  public DateAndTimeFmt(): string {
    return this.getFromConfig('dateAndTime');
  }

  public DateFmtBase(): string {
    return this.getFromConfig('dateFmt');
  }

  public DateFmtWithMonthNoYear(): string {
    return this.getFromConfig('dateAndMonth');
  }

  public DateFmtWithMonthName(): string {
    return this.getFromConfig('dateAndMonthAndYear');
  }

  public DateFmtWithMonthNameYear2Digits(): string {
    return this.getFromConfig('dateAndMonthAndYear2Digits');
  }

  public DateQueryString(): string {
    return this.getFromConfig('dateQueryParams');
  }

  public DateQueryApiFormat(): string {
    return this.getFromConfig('dateQueryApiFormat');
  }

  public GetDateStandardLocaleOpts(): { locale: string } {
    return this.getFromConfig('dateStandardLocaleOpts');
  }

  public GetAmountStandardFormatOpts(): string {
    return this.getFromConfig('amountStandardFormatOpts') ?? this.DEFAULT_FORMATS.amountStandardFormatOpts;
  }

  public GetAmountStandardLocaleOpts(): string {
    return this.getFromConfig('amountStandardLocaleOpts') ?? this.DEFAULT_FORMATS.amountStandardLocaleOpts;
  }

  public getCurrencySymbol(currency?: string): string {
    if (!currency) return this.getFromConfig('currencies')[this.defaultCurrency()];
    return this.getFromConfig('currencies')[currency.trim().toUpperCase()];
  }

  /**
   * Format an amount in a currency
   * ES 12.23 $ / $ 12.23
   *
   * @param {number} amount
   * @param {string} [currency] Currency code
   * @param {string} [format] {minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}
   * @param {boolean} [inverted=false]
   * @return {*}  {string}
   * @memberof CoreFormatService
   */
  public CurrencyAmount(amount: number, currency?: string, format?: string, inverted: boolean = false): string {
    const currencySymbol = this.getCurrencySymbol(currency) || '$';
    const formattedAmount = this.decimalPipe.transform(amount, (!!format ? format : this.GetAmountStandardFormatOpts()), this.GetAmountStandardLocaleOpts());
    return inverted ? `${formattedAmount} ${currencySymbol}` : `${currencySymbol} ${formattedAmount}`;
  }

  /**
   * Format an amount in a currency (inverted)
   * ES $ 12.23
   *
   * @param {number} amount
   * @param {string} currency
   * @param {string} [format] Custom number format
   * @return {*}  {string}
   * @memberof CoreFormatService
   */
  public CurrencyAmountInverted(amount: number, currency: string, format?: string): string {
    return this.CurrencyAmount(amount, currency, format, true);
  }

  /**
   * Format a date in default behavier
   * ES 15 Jul 2022
   *
   * @param {Date} date
   * @return {*}  {string}
   * @memberof CoreFormatService
   */
  public JsDateToDefaultDateFormat(date: Date): string {
    const dt = DateTime.fromJSDate(date);
    if (!dt.isValid) return '';
    return dt.toFormat(this.DateFmtWithMonthName());
  }

  /**
   * Format a date in default behavier
   * ES 15 Jul 2022
   *
   * @param {DateTime} date
   * @return {*}  {string}
   * @memberof CoreFormatService
   */
  public DateToDefaultDateFormat(date: DateTime): string {
    if (!date.isValid) return '';
    return date.toFormat(this.DateFmtWithMonthName());
  }
  /**
   * Format a number to percent
   * Es 12.23%
   *
   * @param {number} value
   * @param {string} [format] Custom number format
   * @return {*}  {string}
   * @memberof CoreFormatService
   */
  public PercentualNumber(value: number, format?: string): string {
    const formattedValue = this.decimalPipe.transform(value, (!!format ? format : '1.2-2'), 'en');
    return `${formattedValue}%`;
  }

  public DateFmtOptsDayNameMonthStartYearDay(): DateTimeFormatOptions {
    return { weekday: 'long', day: '2-digit', month: 'short', year: '2-digit' };
  };

  public TimeFmtOptsHourMinuteAMPM(): DateTimeFormatOptions {
    return DateTime.TIME_SIMPLE;
  }

  public static FormatDateToApi(date: DateTime | undefined): string | null | undefined {
    return date !== undefined && date.isValid ? date.toISODate() : undefined;
  }

  public static ApiFormatToDate(date: string): DateTime | undefined {
    if (date !== undefined) {
      const dateTime = DateTime.fromISO(date);
      if (dateTime.isValid) {
        return dateTime;
      }
    }
    return undefined;
  }

  public static FormatTimeToApi(date: DateTime | undefined): string | null | undefined {
    return date !== undefined && date.isValid ? date.toISOTime({ includeOffset: false }) : undefined;
  }
  /**
   * Check if a string is a valid email
   *
   * @static
   * @param {string} val
   * @return {*}  {boolean}
   * @memberof CoreFormatService
   */
  public static IsEmail(val: string): boolean {
    return !!val && !!val.match("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$");
  }

  public DeeplyConvertDateTimeOfObject(data: any) {

    const DeeplyConvertDateTimeOfObjectProp = (prop: any): any => {

      if (prop !== undefined && prop !== null) {

        if (Array.isArray(prop)) {
          return prop.map(item => DeeplyConvertDateTimeOfObjectProp(item));
        }

        if (prop instanceof DateTime && prop.isValid) {
          return prop.isValid ? prop.toFormat(this.DateQueryApiFormat()) : '';
        }

        if (prop instanceof Date) {
          const offset = prop.getTimezoneOffset();
          const date = new Date(prop.getTime() - (offset * 60 * 1000));
          try {
            return date.toISOString().split('T')[0] + ' ' + date.toISOString().split('T')[1].split('.')[0];
          } catch (e: unknown) {
            return '';
          }
        }

        if (typeof prop === 'object') {

          Object.keys(prop).forEach(key => {
            prop[key] = DeeplyConvertDateTimeOfObjectProp(prop[key]);
          });

          return prop;
        }
      }

      return prop;
    }

    return DeeplyConvertDateTimeOfObjectProp(data);
  }
}
